import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Check, Loader2, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal, Eyebrow } from './Reveal';
import { PACKAGES, CONTACT } from '../constants/site';
import { api } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

function PayPalMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M7.1 21H3.6c-.4 0-.6-.3-.6-.6L6.6 2.7c.1-.4.4-.6.8-.6h6.6c2.8 0 4.7 1.7 4.3 4.4-.5 3.5-2.9 5.3-6.2 5.3H9.4c-.4 0-.7.3-.8.6L7.6 20.5c0 .3-.2.5-.5.5z" />
    </svg>
  );
}

function PlanCard({ pkg, mode }) {
  const [busy, setBusy] = useState('');
  const [zelleOpen, setZelleOpen] = useState(false);
  const [emailFor, setEmailFor] = useState('');
  const [email, setEmail] = useState('');

  const startStripe = async (prospectEmail) => {
    setBusy('stripe');
    try {
      const ref = sessionStorage.getItem('gslab_ref') || '';
      const { data } = await api.post('/payments/checkout', { package_id: pkg.id, origin_url: window.location.origin, prospect_email: prospectEmail, ref });
      window.location.href = data.checkout_url;
    } catch {
      toast.error('Checkout could not start. Please try again.');
      setBusy('');
    }
  };

  const startPaypal = async (prospectEmail) => {
    setBusy('paypal');
    try {
      const { data } = await api.post('/paypal/order', { package_id: pkg.id, origin_url: window.location.origin, prospect_email: prospectEmail, ref: sessionStorage.getItem('gslab_ref') || '' });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PayPal checkout is being linked — use card checkout for now.');
      setBusy('');
    }
  };

  const proceed = (skip) => {
    const action = emailFor;
    const addr = skip ? '' : email;
    setEmailFor('');
    if (action === 'stripe') startStripe(addr);
    else if (action === 'paypal') startPaypal(addr);
  };

  return (
    <div
      data-testid={mode === 'oneTime' ? 'pricing-one-time-card' : 'pricing-subscription-card'}
      className={`relative rounded-3xl p-8 flex flex-col h-full bg-[#0F1C33]/70 ${pkg.featured ? 'gold-ring' : 'border border-slate-700/40'}`}
    >
      {pkg.featured && (
        <span className="absolute -top-3 left-8 font-mono-tech text-[10px] tracking-[0.25em] uppercase bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-4 py-1">
          Most Chosen
        </span>
      )}
      <h3 className="font-display font-bold text-xl">{pkg.name}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{pkg.blurb}</p>
      <div className="mt-6 mb-7">
        <span className="font-display font-extrabold text-4xl text-gradient-gold">${pkg.price.toLocaleString()}</span>
        <span className="font-mono-tech text-xs text-slate-500 ml-2">{pkg.unit}</span>
      </div>
      <ul className="space-y-3 mb-8">
        {pkg.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
            <Check size={15} className="text-amber-400 mt-0.5 shrink-0" />{f}
          </li>
        ))}
      </ul>
      <div className="mt-auto space-y-3">
        <button
          data-testid="pricing-stripe-checkout-btn"
          onClick={() => setEmailFor('stripe')}
          disabled={!!busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-60"
        >
          {busy === 'stripe' ? <Loader2 size={17} className="animate-spin" /> : <CreditCard size={17} />}
          Card · Apple Pay · Google Pay
        </button>
        {mode === 'oneTime' && (
          <button
            data-testid="pricing-paypal-checkout-btn"
            onClick={() => setEmailFor('paypal')}
            disabled={!!busy}
            className="w-full inline-flex items-center justify-center gap-2 border border-slate-600/70 hover:border-sky-400/60 text-slate-200 rounded-full px-6 py-3 transition-colors duration-300 disabled:opacity-60"
          >
            {busy === 'paypal' ? <Loader2 size={17} className="animate-spin" /> : <PayPalMark />}
            Pay with PayPal
          </button>
        )}
        <button
          data-testid="pricing-zelle-info-btn"
          onClick={() => setZelleOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-amber-300 transition-colors py-1"
        >
          <Landmark size={15} /> Prefer Zelle? See instructions
        </button>
      </div>

      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor('')}>
        <DialogContent className="bg-[#0B1528] border-amber-500/25 text-slate-200" data-testid="checkout-email-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Where should we send your receipt?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Optional — we'll email your receipt and project kickoff details here. It also lets us send you a reminder if checkout gets interrupted.
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="checkout-email-input"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#050B14]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 mt-2"
          />
          <div className="flex gap-3 mt-4">
            <button
              data-testid="checkout-email-continue-btn"
              onClick={() => proceed(false)}
              className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-5 py-3 text-sm"
            >
              Continue to checkout
            </button>
            <button
              data-testid="checkout-email-skip-btn"
              onClick={() => proceed(true)}
              className="text-sm text-slate-400 hover:text-slate-200 px-4 transition-colors"
            >
              Skip
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={zelleOpen} onOpenChange={setZelleOpen}>
        <DialogContent className="bg-[#0B1528] border-amber-500/25 text-slate-200" data-testid="zelle-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Pay with Zelle — {pkg.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Zelle transfers are direct bank-to-bank, with no processing fees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm mt-2">
            <div className="border border-slate-700 rounded-xl p-4 space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">Send to</span><span className="font-mono-tech text-amber-300">{CONTACT.phone}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Recipient</span><span>Gabe Kim — GSLAB</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-semibold">${pkg.price.toLocaleString()}{pkg.unit === '/month' ? ' / month' : ''}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Memo</span><span>Your business name</span></div>
            </div>
            <p className="text-slate-400 leading-relaxed">
              After sending, email <a href={`mailto:${CONTACT.email}`} className="text-amber-300 hover:underline">{CONTACT.email}</a> with
              your business name and chosen plan — your project kicks off within one business day.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Pricing() {
  const [mode, setMode] = useState('oneTime');
  const plans = PACKAGES[mode];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 bg-[#0B1528]/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal className="text-center">
          <Eyebrow>Transparent Pricing</Eyebrow>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Own it outright, or <span className="text-gradient-gold">subscribe and grow.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
            Two ways to work with GSLAB — pick a one-time custom build, a managed monthly plan, or both.
            Comparable agency builds run $5,000–$15,000+ and freelancers rarely include automation or hosting care.
          </p>
        </Reveal>

        <Reveal i={1} className="mt-10 flex justify-center">
          <div className="inline-flex border border-slate-700 rounded-full p-1 bg-[#050B14]/60" data-testid="pricing-mode-toggle">
            {[['oneTime', 'One-Time Build'], ['subscription', 'Monthly Care Plan']].map(([m, label]) => (
              <button
                key={m}
                data-testid={`pricing-toggle-${m === 'oneTime' ? 'onetime' : 'subscription'}`}
                onClick={() => setMode(m)}
                className={`relative px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  mode === m ? 'text-[#050B14]' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {mode === m && (
                  <motion.span layoutId="pricing-pill" className="absolute inset-0 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3C64F]" transition={{ type: 'spring', stiffness: 300, damping: 30 }} />
                )}
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
          <AnimatePresence mode="wait">
            {plans.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <PlanCard pkg={pkg} mode={mode} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <Reveal i={3} className="mt-10 text-center">
          <p className="text-xs text-slate-500 font-mono-tech tracking-wider max-w-xl mx-auto leading-relaxed">
            SECURE CHECKOUT BY STRIPE — VISA · MASTERCARD · AMEX · APPLE PAY · GOOGLE PAY ·
            PAYPAL AVAILABLE AT CHECKOUT · ZELLE ACCEPTED DIRECTLY. PAIR ANY BUILD WITH A CARE PLAN FOR FULLY MANAGED PEACE OF MIND.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
